import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm({ name: "", email: "", company: "", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 bg-stone">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left — Text */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            custom={0}
          >
            <span className="font-body text-xs uppercase tracking-[0.2em] text-charcoal/50 block mb-6">
              Contact
            </span>
            <h2
              data-testid="contact-heading"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] text-deep-black leading-[1.1] font-light mb-6"
            >
              Let's build something{" "}
              <span className="font-medium">retail-ready.</span>
            </h2>
            <p className="font-body text-base text-charcoal/60 max-w-md leading-relaxed font-light">
              Whether you're launching a new product, refreshing a brand, or
              rethinking your retail strategy — we'd love to hear from you.
            </p>
          </motion.div>

          {/* Right — Form */}
          <motion.form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeUp}
            custom={1}
            className="flex flex-col gap-5"
          >
            <input
              type="text"
              name="name"
              placeholder="Your name"
              value={form.name}
              onChange={handleChange}
              data-testid="contact-name-input"
              required
              className="font-body text-sm rounded-full bg-transparent border border-charcoal/15 px-6 py-4 text-deep-black placeholder:text-charcoal/30 focus:border-charcoal/50 transition-colors duration-300"
            />
            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={form.email}
              onChange={handleChange}
              data-testid="contact-email-input"
              required
              className="font-body text-sm rounded-full bg-transparent border border-charcoal/15 px-6 py-4 text-deep-black placeholder:text-charcoal/30 focus:border-charcoal/50 transition-colors duration-300"
            />
            <input
              type="text"
              name="company"
              placeholder="Company (optional)"
              value={form.company}
              onChange={handleChange}
              data-testid="contact-company-input"
              className="font-body text-sm rounded-full bg-transparent border border-charcoal/15 px-6 py-4 text-deep-black placeholder:text-charcoal/30 focus:border-charcoal/50 transition-colors duration-300"
            />
            <textarea
              name="message"
              placeholder="Tell us about your project"
              value={form.message}
              onChange={handleChange}
              data-testid="contact-message-input"
              required
              rows={4}
              className="font-body text-sm rounded-2xl bg-transparent border border-charcoal/15 px-6 py-4 text-deep-black placeholder:text-charcoal/30 focus:border-charcoal/50 transition-colors duration-300 resize-none"
            />
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={status === "sending"}
              className="font-body text-sm tracking-wide rounded-full bg-deep-black text-white py-4 px-8 hover:bg-charcoal hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {status === "sending"
                ? "Sending..."
                : status === "sent"
                ? "Message sent"
                : status === "error"
                ? "Something went wrong"
                : "Send message"}
            </button>
            {status === "sent" && (
              <p data-testid="contact-success-message" className="font-body text-sm text-charcoal/60 text-center font-light">
                Thank you. We'll be in touch shortly.
              </p>
            )}
          </motion.form>
        </div>

        {/* HOLT Branding — bottom right of section */}
        <div className="flex items-end justify-end gap-4 mt-16 pr-3" data-testid="contact-holt-branding">
          <span className="font-heading text-sm tracking-[0.05em] uppercase text-charcoal/70">
            <span className="font-bold">LMT SPARK</span>, A House of L Toys Company
          </span>
          <img
            src="https://customer-assets.emergentagent.com/job_lmt-product-strategy/artifacts/2zitti51_LOGO%202.png"
            alt="House of L Toys"
            className="h-16 w-auto object-contain"
          />
        </div>
      </div>
    </section>
  );
};
